// @ts-nocheck
/**
 * areaAnalysisFlows.ts
 * エリア分析フロー関連メソッド
 * App.vue から切り出し
 */

import * as turf from '@turf/turf'
import * as Layers from '@/js/layers'
import { startAreaAnalysisFreehand } from '@/js/areaAnalyzer'
import { requestAddLayers } from '@/js/layerEngine/addLayersGate'

type VM = any

export function enableAreaAnalysisEvacuationLayerOnce(vm: VM) {
  try {
    if (vm._aiAreaAnalysisEvacuationLayerInitialized) return
    const tryEnable = () => {
      return addSelectedLayerById(vm, 'oh-reinfolib-hinanbasho')
    }
    const tryLater = (delayMs) => {
      setTimeout(() => {
        if (tryEnable()) {
          vm._aiAreaAnalysisEvacuationLayerInitialized = true
        }
      }, delayMs)
    }
    if (tryEnable()) {
      vm._aiAreaAnalysisEvacuationLayerInitialized = true
    } else {
      tryLater(300)
      tryLater(800)
      tryLater(1500)
    }
  } catch (_) {
    // noop
  }
}
export function addSelectedLayerById(vm: VM, layerId) {
  const node = findLayerById(vm, Layers.layers.map01, layerId)
  if (!node) return false

  const selectedLayers = vm.$store.state.selectedLayers.map01
  if (!selectedLayers || !Array.isArray(selectedLayers)) return false
  const isActive = selectedLayers.some((l) => l.id === layerId)
  if (isActive) return true

  const layerObj = {
    id: node.id,
    label: node.label,
    source: node.source,
    sources: node.sources,
    layer: node.layer,
    layers: node.layers,
    opacity: 0.8,
    visibility: true,
    attribution: node.attribution,
    ext: node.ext,
    info: node.info,
    position: node.position,
  }

  requestAddLayers(vm.$store, 'map01')
  if (!selectedLayers.some(layer => layer.id === 'oh-amx-a-fude' || layer.id === 'oh-homusyo-2025-layer')) {
    selectedLayers.unshift(layerObj)
  } else {
    selectedLayers.splice(1, 0, layerObj)
  }
  // selectedLayersの更新を明示してDialog-layer側の監視を確実に発火させる
  vm.$store.state.selectedLayers = {
    ...vm.$store.state.selectedLayers,
    map01: [...selectedLayers],
  }
  return true
}
export function removeSelectedLayerById(vm: VM, layerId) {
  const selectedLayers = vm.$store.state.selectedLayers.map01
  if (!selectedLayers || !Array.isArray(selectedLayers)) return false
  const index = selectedLayers.findIndex((layer) => layer.id === layerId)
  if (index === -1) return false
  requestAddLayers(vm.$store, 'map01')
  selectedLayers.splice(index, 1)
  vm.$store.state.selectedLayers = {
    ...vm.$store.state.selectedLayers,
    map01: [...selectedLayers],
  }
  return true
}
export function findLayerById(vm: VM, tree, targetId) {
  if (!tree) return null
  for (const node of tree) {
    if (node.id === targetId) return node
    if (node.nodes) {
      const found = findLayerById(vm, node.nodes, targetId)
      if (found) return found
    }
    if (node.children) {
      const found = findLayerById(vm, node.children, targetId)
      if (found) return found
    }
  }
  return null
}
export async function selectAreaAnalysisMode(vm: VM, mode) {
  vm.showAreaAnalysisModeDialog = false
  if (mode === 'freehand') {
    await startAreaAnalysisFreehandFlow(vm)
    return
  }
  if (mode === 'circle') {
    await startAreaAnalysisCircleFlow(vm)
    return
  }
  if (mode === 'polygon') {
    await startAreaAnalysisPolygonFlow(vm)
    return
  }
  if (mode === 'point') {
    vm.beginAreaAnalysisPointSelection()
  }
}
// 面積チェック共通メソッド（800,000 m²を上限）
export function checkAreaLimit(vm: VM, polygon) {
  const MAX_AREA = 800000 // m²
  const area = turf.area(polygon)
  if (area > MAX_AREA) {
    vm.areaTooLargeValue = (area / 10000).toFixed(1)
    vm.showAreaTooLargeDialog = true
    return false
  }
  return true
}
export async function startAreaAnalysisFreehandFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return

  const mapContainer = document.getElementById('map01')
  if (mapContainer) {
    mapContainer.classList.remove('ai-area-analysis-point-cursor')
  }

  const polygon = await startAreaAnalysisFreehand(map)
  if (polygon) {
    if (!checkAreaLimit(vm, polygon)) return
    // フィーチャー確定時点でshareFeaturesに追加
    vm.$store.commit('addShareFeature', {
      aiType: 'risk',
      geometry: polygon.geometry,
      properties: {}
    })
    vm.$store.commit('addFWInstance', {
      type: 'area-analysis',
      data: { polygonData: polygon, analysisMode: 'freehand' },
    })
  }
}
export async function startAreaAnalysisCircleFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return

  const mapContainer = document.getElementById('map01')
  if (mapContainer) {
    mapContainer.classList.remove('ai-area-analysis-point-cursor')
  }

  const { startAreaAnalysisCircle } = await import('@/js/areaAnalyzer')
  const polygon = await startAreaAnalysisCircle(map)
  if (polygon) {
    if (!checkAreaLimit(vm, polygon)) return
    // フィーチャー確定時点でshareFeaturesに追加
    vm.$store.commit('addShareFeature', {
      aiType: 'risk',
      geometry: polygon.geometry,
      properties: {}
    })
    vm.$store.commit('addFWInstance', {
      type: 'area-analysis',
      data: { polygonData: polygon, analysisMode: 'circle' },
    })
  }
}
export async function startAreaAnalysisPolygonFlow(vm: VM) {
  const map = vm.$store.state.map01
  if (!map) return

  const mapContainer = document.getElementById('map01')
  if (mapContainer) {
    mapContainer.classList.remove('ai-area-analysis-point-cursor')
  }

  const { startAreaAnalysisPolygon } = await import('@/js/areaAnalyzer')
  const polygon = await startAreaAnalysisPolygon(map)
  if (polygon) {
    if (!checkAreaLimit(vm, polygon)) return
    // フィーチャー確定時点でshareFeaturesに追加
    vm.$store.commit('addShareFeature', {
      aiType: 'risk',
      geometry: polygon.geometry,
      properties: {}
    })
    vm.$store.commit('addFWInstance', {
      type: 'area-analysis',
      data: { polygonData: polygon, analysisMode: 'polygon' },
    })
  }
}